/**
 * Nexgo Experiences Section Component
 * Displays horizontal scrollable experience cards (small) or grid layout (large)
 * Supports both small (180px) and large (220px grid) card variants
 * Uses ScrollableCardList for consistent horizontal scrolling with navigation arrows
 */

import React from 'react';
import { SmallExperienceCard } from './SmallExperienceCard';
import { GridExperienceCard } from './GridExperienceCard';
import { spacing } from '@/design-system/utils';
import { ExperienceCard as ExperienceCardType } from '@/constants/chatScreenData';
import { CollapsibleSection } from '@/components/ui/collapsible-section';
import { ScrollableCardList } from '@/components/ui/scrollable-card-list';

export interface NexgoExperiencesSectionProps {
  experiences: ExperienceCardType[];
  onCardClick?: (cardId: string) => void;
  /**
   * Card size variant
   * 'small' - 180px × 160px (for chat/discover screen) - horizontal scroll
   * 'large' - 220px grid cards (for experiences tab) - 3 column grid
   * @default 'small'
   */
  cardSize?: 'small' | 'large';
  /**
   * Whether the section should be collapsed by default
   * @default false
   */
  defaultCollapsed?: boolean;
  /**
   * Controlled collapsed state. If provided, component becomes controlled.
   */
  isCollapsed?: boolean;
  /**
   * Callback when collapse button is clicked (for controlled mode)
   */
  onToggle?: (collapsed: boolean) => void;
}

export const NexgoExperiencesSection: React.FC<
  NexgoExperiencesSectionProps
> = ({
  experiences,
  onCardClick,
  cardSize = 'small',
  defaultCollapsed = false,
  isCollapsed,
  onToggle,
}) => {
  // Select card component based on size variant
  const CardComponent =
    cardSize === 'large' ? GridExperienceCard : SmallExperienceCard;

  // For small cards, use ScrollableCardList with CollapsibleSection (like PlacesOfRelevanceSection)
  if (cardSize === 'small') {
    return (
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[4],
        }}
      >
        <CollapsibleSection
          title='Nexgo Experiences'
          defaultCollapsed={defaultCollapsed}
          isCollapsed={isCollapsed}
          onToggle={onToggle}
        >
          <ScrollableCardList>
            {experiences.map(experience => (
              <CardComponent
                key={experience.id}
                experience={experience}
                onClick={() => onCardClick?.(experience.id)}
              />
            ))}
          </ScrollableCardList>
        </CollapsibleSection>
      </div>
    );
  }

  // For large cards, use grid layout (for experiences tab) - match experience page: 220px fixed width per card
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[4],
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, 220px)',
          columnGap: spacing[4],
          rowGap: spacing[6],
          width: '100%',
          justifyContent: 'start',
        }}
      >
        {experiences.map(experience => (
          <CardComponent
            key={experience.id}
            experience={experience}
            onClick={() => onCardClick?.(experience.id)}
          />
        ))}
      </div>
    </div>
  );
};
